import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Col, Flex, Form, message, Row } from 'antd';
import { useTranslation } from 'react-i18next';

import DashboardTitle from '../../../layout/DashboardTitle';
import dayjs from 'dayjs';
import { CustomParagraph } from '../../../components';
import EmptyShipments from '../EmptyShipments';
import ShipmentCard from '../ShipmentCard';
import ShipmentDrawer from '../ShipmentDrawer';
import {
  GetHasShipmentBookingTemplateSelection,
  GetShipmentSummary,
  GetShipmentsVersion,
  PostShipments,
} from '../../../apis/shipment';
import type { SingleShipmentDataExtended } from '../../../apis/types';
import { useToken } from '../../../common/helper';
import { hideNewShipmentButtonForCompany } from '../../../common/constants';
import SavedFilterSetModal from './components/TabStrip/SavedFilterSetModal';
import { useShipmentSavedFilterSetModal } from './hooks/useShipmentSavedFilterSetModal';
import BulkUploadModal from '../../Modals/BulkUploadModal.jsx';
import CustomPagination from '../../../components/CustomPagination';
import CommonDrawer from '../../../components/CommonDrawer';
import CreateShipmentDrawer from '../CreateShipmentDrawer';
import BookShipmentDrawer from '../BookShipmentDrawer';
import QuoteShipmentDrawer from '../QuoteShipmentDrawer';
import CreateShellShipmentDrawer from '../CreateShellShipmentDrawer';
import AnimatedShipmentCard from '../partials/AnimatedShipmentCard';
import ShipmentToolbar from '../ShipmentToolbar/ShipmentToolbar.jsx';
import useUnauthorize from '../../../hooks/useUnauthorize';
import ShipmentsFilterPanel from './components/ShipmentFiltersPanel';

import type { CalendarApi } from '@fullcalendar/core';

import {
  Shipment,
  SummaryData,
  DrawerStateFromHome,
  DrawerState,
  ShipmentRequestBody,
  ShipmentsApiResponse,
  ShipmentsVersionApiResponse,
  ToggleDrawerFn,
  AppliedFilter,
  FilterArrayItem,
  SummaryApiResponse,
  ShipmentFiltersPayload,
  CalendarShipmentItem,
  ID,
  CalendarViewBy,
  ShipmentTab,
} from './types';

import { useShipmentsEffects } from './hooks/useShipmentEffects';
import { useShipmentSelection } from './hooks/useShipmentSelection';
import { useShipmentPagination } from './hooks/useShipmentPagination';
import { useShipmentPermissions } from './hooks/useShipmentPermissions';
import { useManualShipmentCreationFlags } from '@/hooks/useManualShipmentCreationFlags';
import SummaryCards from './components/SummaryCards';
import { useShipmentsFiltersController } from './hooks/useShipmentFiltersController';
import { CalendarView } from './components/calendar/CalendarView';
import SwitchButtons from './components/SwitchButtons';
import { useCalendarShipments } from './hooks/useCalendarShipments';
import CalendarViewMore from './components/calendar/CalendarViewMore';
import { useCalendarViewportCounts } from './hooks/useCalendarViewPortCounts';
import { useDispatch, useSelector } from 'react-redux';
import { clearShipmentNav } from '@/redux-toolkit/slice/shipmentNavigationSlice';
import { setActiveTabId } from '@/redux-toolkit/slice/shipmentTabSlice';
import { RootState } from '@/redux-toolkit/store';
import { ZOOMLOGI_CLOSE_UI_OVERLAYS_EVENT } from '@/common/zoomlogiUiEvents';
import { CalendarIcon, ListIcon } from '@/assets/icons/icons';
import { buildShipmentFiltersPayload } from './utils/buildShipmentFiltersPayload';
import { buildMassActionFiltersPayload } from './utils/buildMassActionFiltersPayload';
import { getSelectionScopeCount } from './utils/getSelectionScopeCount';
import { getVisibleCalendarShipmentIds } from './utils/calendarSelection';
import TabStrip from './components/TabStrip';
import useViewFiltersModal from './hooks/useViewFilterModal';
import ViewFiltersModal from './components/ViewFilterModal';
import useFetchCompanyCouriers from '../../../hooks/useFetchCompanyCouriers';
import { hasQuickStatCompanyCourier } from '../../../common/utils/quickStatCompanyCouriers';
import { isWorldCourierCompany } from '../../../common/constants/worldCourier';
import { isCryoportCompany } from '../../../common/constants/cryoport';

const LIST_POLL_INTERVAL_MS = 60000; // 1 minute
const LIST_FORCE_REFRESH_MS = 120000;

const Shipments: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { token, decodedUserToken } = useToken();
  const decodedToken = decodedUserToken();
  const worldCourierEnabled = useMemo(
    () => isWorldCourierCompany(decodedToken?.enablecustomerleg),
    [decodedToken?.enablecustomerleg]
  );
  const cryoportEnabled = useMemo(
    () => isCryoportCompany(decodedToken?.company_id),
    [decodedToken?.company_id]
  );
  const hideNewShipmentButton = useMemo(
    () => hideNewShipmentButtonForCompany(decodedToken?.company_id),
    [decodedToken?.company_id]
  );
  const manualShipmentFlags = useManualShipmentCreationFlags();
  const { unauthorize } = useUnauthorize();
  const { t } = useTranslation('shipments');
  const { allPermissions, permissions, isSupportCompany } =
    useShipmentPermissions();
  const { couriers } = useFetchCompanyCouriers();
  const hasQuickStat = useMemo(
    () => hasQuickStatCompanyCourier(couriers),
    [couriers]
  );

  /** At least one booking template row (template_selection) for QuickSTAT book menu. */
  const [bookingTemplatesLoading, setBookingTemplatesLoading] = useState(true);
  const [hasBookingTemplates, setHasBookingTemplates] = useState(false);

  useEffect(() => {
    if (!token) {
      setBookingTemplatesLoading(false);
      setHasBookingTemplates(false);
      return;
    }
    let cancelled = false;
    setBookingTemplatesLoading(true);
    GetHasShipmentBookingTemplateSelection(token)
      .then((res) => {
        const has = Boolean(res?.data?.data?.data?.has_template_selection);
        if (!cancelled) setHasBookingTemplates(has);
      })
      .catch(() => {
        if (!cancelled) setHasBookingTemplates(false);
      })
      .finally(() => {
        if (!cancelled) setBookingTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  /**
   * Book is greyed unless World Courier is allowed, or QuickStat is linked
   * and (templates still loading, or at least one template exists).
   * Company Settings can hide Book entirely by unchecking it.
   */
  const bookShipmentMenuDisabled = useMemo(() => {
    if (worldCourierEnabled) return false;
    if (!hasQuickStat) return true;
    if (bookingTemplatesLoading) return false;
    return !hasBookingTemplates;
  }, [
    worldCourierEnabled,
    hasQuickStat,
    bookingTemplatesLoading,
    hasBookingTemplates,
  ]);
  const filters = useShipmentsFiltersController();
  const {
    setAppliedFilters,
    setFilterArray,
    setAppliedSearchQuery,
    setActiveViewType,
  } = filters;
  const dispatch = useDispatch();
  const shipmentNav = useSelector(
    (state: RootState) => state.shipmentNavigation
  );
  const { customTabs, additionalFiltersPerTab, activeTabId } = useSelector(
    (state: RootState) => state.shipmentTab
  );
  const fetchRef = useRef<
    (
      searchquery: string,
      check: boolean,
      pageArg?: number,
      pageSizeArg?: number,
      calledFrom?: string,
      useMissingIds?: boolean,
      missingIdsOverride?: string[] | null,
      payloadOverride?: ShipmentFiltersPayload | null,
      tabIdOverride?: string | null
    ) => Promise<unknown> | void
  >(() => Promise.resolve());

  const [_selectedShipmentID, setSelectedShipmentID] = useState<string | null>(
    null
  );
  const calendarApiRef = useRef<CalendarApi | null>(null);
  const [shipmentsData, setShipmentsData] = useState<Shipment[]>([]);
  const [summaryData, setSummaryData] = useState<SummaryData>({});
  const [selectAllAcrossFilters, setSelectAllAcrossFilters] = useState(false);
  const [excludedIds, setExcludedIds] = useState<string[]>([]);

  const [drawerStateFromHome, setDrawerStateFormHome] =
    useState<DrawerStateFromHome>({
      dState: location?.state?.drawerState ?? '',
      shipmentId: location?.state?.shipmentId ?? null,
      cocId: location?.state?.CoC_ID ?? null,
    });

  const [filteredData, setFilteredData] = useState<Shipment[]>(shipmentsData);
  const [showBulkUploadModal, setShowBulkUploadModal] =
    useState<boolean>(false);
  const [startAlertsInterval] = useState<boolean>(true);

  const isSearchTriggered = useMemo(() => {
    const applied = (filters.appliedSearchQuery ?? '').trim();
    return applied.length > 0;
  }, [filters.appliedSearchQuery]);

  const [highlightedShipmentId, setHighlightedShipmentId] = useState<
    string | null
  >(null);
  const [isLoadingShipments, setIsLoadingShipments] = useState<boolean>(true);

  const [drawerState, setDrawerState] = useState<DrawerState>({
    isOpen: false,
    name: '',
    shipmentId: null,
    alert: null,
  });
  const [drawerLegType, setDrawerLegType] = useState<'CL' | 'EL'>();
  const [overAllShipmentCount, setOverAllShipmentCount] = useState<number>(0);
  const [newShipmentData, setNewShipmentData] = useState<Shipment[]>([]);
  const [openCreateShipmentDrawer, setOpenCreateShipmentDrawer] =
    useState<boolean>(false);
  const [openBookShipmentDrawer, setOpenBookShipmentDrawer] =
    useState<boolean>(false);
  const [openQuoteShipmentDrawer, setOpenQuoteShipmentDrawer] =
    useState<boolean>(false);
  const [quotePrefillShipment, setQuotePrefillShipment] = useState<
    SingleShipmentDataExtended | undefined
  >(undefined);
  const [quoteDrawerMode, setQuoteDrawerMode] = useState<'quote' | 'return'>(
    'quote'
  );
  const [bookPrefillShipment, setBookPrefillShipment] = useState<
    SingleShipmentDataExtended | undefined
  >(undefined);
  const [openCreateShellDrawer, setOpenCreateShellDrawer] =
    useState<boolean>(false);
  const [detailRefreshKey, setDetailRefreshKey] = useState<number>(0);
  const [moreDrawerEventDatetime, setMoreDrawerEventDatetime] =
    useState<string>('');
  const [moreDrawerViewBy, setMoreDrawerViewBy] =
    useState<CalendarViewBy>('arrival');

  const activeViewType = filters.activeViewType ?? 'list';

  const isCalendarActive = activeViewType === 'calendar';
  const maxSelect = activeViewType === 'calendar' ? null : 50;

  const isCalendarActiveRef = useRef(isCalendarActive);
  useEffect(() => {
    isCalendarActiveRef.current = isCalendarActive;
  }, [isCalendarActive]);

  const didConsumeHomeCreateDrawerIntentRef = useRef(false);
  useEffect(() => {
    if (didConsumeHomeCreateDrawerIntentRef.current) return;

    const state = (location.state ?? {}) as Record<string, unknown>;
    const shouldOpen =
      state.__zl_open_create_shipment_drawer === true &&
      state.__zl_source === 'home_empty_shipments';

    if (!shouldOpen) return;
    if (!manualShipmentFlags.loaded) return;

    didConsumeHomeCreateDrawerIntentRef.current = true;
    if (manualShipmentFlags.track) {
      setOpenCreateShipmentDrawer(true);
    }

    const {
      __zl_open_create_shipment_drawer: _ignore1,
      __zl_source: _ignore2,
      ...rest
    } = state as Record<string, unknown>;

    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: rest }
    );
  }, [
    location.hash,
    location.pathname,
    location.search,
    location.state,
    manualShipmentFlags.loaded,
    manualShipmentFlags.track,
    navigate,
  ]);

  const {
    viewFiltersTab,
    viewFiltersModalOpen,
    handleOpenViewFiltersModal,
    handleCloseViewFiltersModal,
  } = useViewFiltersModal();

  const {
    selectedIds,
    setSelectedIds,
    selectedShipmentStatuses,
    setSelectedShipmentStatuses,
    checked,
    setChecked,
    handleSelect,
  } = useShipmentSelection(maxSelect);

  const {
    page,
    setPage,
    pageSize,
    totalPages,
    setTotalPages,
    totalShipments,
    setTotalShipments,
  } = useShipmentPagination(1, 50);

  const shipmentsVersionRef = useRef<number | null>(null);
  const lastSummaryPayloadRef = useRef<string | null>(null);
  const lastShipmentsFetchRef = useRef<number>(0);
  const lastSummaryFetchRef = useRef<number>(0);
  const pageRef = useRef<number>(page);
  const moreDrawerPageRef = useRef<number>(1);
  const pageSizeRef = useRef<number>(pageSize);
  const isTabVisibleRef = useRef<boolean>(!document.hidden);
  const [drawerSource, setDrawerSource] = useState<'list' | 'calendar'>('list');
  const [moreDrawerSource, setMoreDrawerSource] = useState<
    'header' | 'slot' | 'month' | undefined
  >(undefined);

  const searchVisibleRef = useRef<boolean>(filters.isSearchVisible);
  const searchQueryRef = useRef<string>(filters.appliedSearchQuery ?? '');
  const [moreDrawerOpen, setMoreDrawerOpen] = useState(false);
  const moreDrawerSortRef = useRef<{
    key: string;
    order: 'asc' | 'desc';
  } | null>(null);
  const didRunInitialFetchRef = useRef(false);
  const didInitialQueryStringEffect = useRef(false);
  const [moreDrawerSlotStart, setMoreDrawerSlotStart] = useState<string | null>(
    null
  );
  const [moreDrawerSlotEnd, setMoreDrawerSlotEnd] = useState<string | null>(
    null
  );
  const returnToMoreDrawerRef = useRef(false);

  const calendarTeamIds = useMemo(() => {
    const team = (filters.filterArray ?? []).find((f) => f.filterBy === 'team');
    return Array.isArray(team?.filterValue) ? team!.filterValue : [];
  }, [filters.filterArray]);

  const calendarCourierIds = useMemo(() => {
    const courier = (filters.filterArray ?? []).find(
      (f) => f.filterBy === 'courier'
    );
    return Array.isArray(courier?.filterValue) ? courier!.filterValue : [];
  }, [filters.filterArray]);

  const calendarItemToShipment = (item: CalendarShipmentItem): Shipment => {
    return {
      id: String(item.shipment_id),
      status: item.status as Shipment['status'],
    } as Shipment;
  };
  const dynamicDropdownHeight = `calc(100vh - 350px)`;
  const buildShipmentRequestBody = useCallback(
    (
      searchquery: string,
      pageVal: number,
      pageSizeVal: number,
      payloadOverride?: ShipmentFiltersPayload | null,
      tabIdOverride?: string | null
    ): ShipmentRequestBody => {
      const trimmedSearch = (searchquery || '').trim();
      const effectiveTabId = tabIdOverride ?? activeTabId;

      const finalPayload = (() => {
        if (payloadOverride === null) {
          return {};
        }

        if (payloadOverride && typeof payloadOverride === 'object') {
          return payloadOverride;
        }

        const customTab = customTabs.find((tab) => tab.id === effectiveTabId);

        if (customTab) {
          const baseAppliedFilters =
            customTab.savedFilterSet?.appliedFilters ?? [];
          const baseFilters = customTab.savedFilterSet?.filters ?? [];

          return (
            buildShipmentFiltersPayload(
              [...baseFilters, ...(filters.filterArray ?? [])] as never,
              [...baseAppliedFilters, ...(filters.appliedFilters ?? [])]
            ) ?? {}
          );
        }

        const rebuiltPayload =
          buildShipmentFiltersPayload(
            (filters.filterArray ?? []) as never,
            filters.appliedFilters ?? []
          ) ?? {};

        if (Object.keys(rebuiltPayload).length > 0) {
          return rebuiltPayload;
        }

        if (
          filters.filtersPayloadRef.current &&
          typeof filters.filtersPayloadRef.current === 'object'
        ) {
          return filters.filtersPayloadRef.current;
        }

        return {};
      })();

      return {
        page: pageVal,
        page_size: pageSizeVal,
        search: trimmedSearch || '',
        ...(effectiveTabId === 'active' || effectiveTabId === 'all_shipments'
          ? { tab_id: effectiveTabId }
          : {}),
        ...finalPayload,
      };
    },
    [
      activeTabId,
      customTabs,
      filters.appliedFilters,
      filters.filterArray,
      filters.filtersPayloadRef,
    ]
  );

  const newShipmentIdsRef = useRef<string[]>([]);

  const fetchShipments = useCallback(
    (
      searchquery: string,
      check: boolean,
      pageArg: number = 1,
      pageSizeArg: number = 50,
      calledFrom?: string,
      useMissingIds: boolean = false,
      missingIdsOverride?: string[] | null,
      payloadOverride?: ShipmentFiltersPayload | null,
      tabIdOverride?: string | null
    ) => {
      if (isCalendarActiveRef.current) {
        return Promise.resolve();
      }
      if (check) {
        setIsLoadingShipments(true);
      }

      const body = buildShipmentRequestBody(
        searchquery,
        pageArg,
        pageSizeArg,
        payloadOverride,
        tabIdOverride
      );

      const finalIds =
        useMissingIds && missingIdsOverride?.length ? missingIdsOverride : [];

      const payload = {
        ...body,
        ...(finalIds.length > 0 ? { shipments: finalIds } : {}),
      };

      return PostShipments(token, payload)
        .then((res: unknown) => {
          const apiRes = res as ShipmentsApiResponse;
          const data = apiRes?.data ?? null;
          const inner = data?.data;
          if (!inner) return;

          if (inner.is_success) {
            lastShipmentsFetchRef.current = Date.now();
            setTotalShipments(inner.total_shipment_count);
            setOverAllShipmentCount(inner.over_all_shipment_count ?? 0);
            setTotalPages(inner.page_count);

            const resp = inner.data ?? [];
            setFilteredData([]);

            if (resp.length > 0) {
              const newIds = newShipmentIdsRef.current;
              const filteredResp = resp.filter(
                (item) => !newIds.includes(item.id)
              );

              setFilteredData(filteredResp);
              setShipmentsData(resp.length === 1 ? resp : filteredResp);
            } else {
              setFilteredData([]);
              setShipmentsData([]);
            }
          } else if (inner.message) {
            message.error(inner.message);
          }
        })
        .catch((error: unknown) => {
          unauthorize(error);
        })
        .finally(() => {
          setIsLoadingShipments(false);
        });
    },
    [
      buildShipmentRequestBody,
      token,
      unauthorize,
      setTotalPages,
      setTotalShipments,
    ]
  );

  const effectiveOpenShipmentId = !drawerState.isOpen
    ? null
    : highlightedShipmentId
      ? null
      : drawerState.shipmentId;

  const handleHighlightShipmentCard = (shipmentId: string) => {
    setHighlightedShipmentId(shipmentId);
    toggleDrawer('open', 'Detail', shipmentId);

    const el = document.getElementById(`shipment-card-${shipmentId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(() => {
      const validPage = newPage > 0 ? newPage : 1;
      fetchShipments(
        filters.appliedSearchQuery ?? '',
        true,
        validPage,
        pageSize
      );
      return validPage;
    });
  };

  const filterByLinkCode = (linkCode: string | null) => {
    if (!linkCode) return;
    const option: AppliedFilter = {
      id: linkCode,
      name: `Group: ${linkCode}`,
      filterBy: 'link_code',
      source: 'manual' as const,
    };

    filters.setAppliedFilters((prevState: AppliedFilter[]) => {
      const alreadyExists = prevState.findIndex(
        (item) => item.id === option.id
      );
      if (alreadyExists === -1) return [...prevState, option];
      return prevState;
    });

    filters.setFilterArray((prev) => {
      const existing = prev.find((f) => f.filterBy === 'link_code');
      if (existing) {
        const existingValues = Array.isArray(existing.filterValue)
          ? existing.filterValue
          : [existing.filterValue];

        if (existingValues.includes(linkCode)) return prev;

        return prev.map((f) =>
          f.filterBy === 'link_code'
            ? { ...f, filterValue: [...existingValues, linkCode] }
            : f
        );
      }

      return [
        ...prev,
        {
          filterBy: 'link_code',
          filterValue: [linkCode],
          source: 'manual' as const,
        },
      ];
    });
  };

  const handleBulkUpload = () => {
    setShowBulkUploadModal(true);
  };

  const handleShipmentFormModal = () => {
    setOpenCreateShipmentDrawer(true);
  };

  const handleBookShipment = () => {
    setBookPrefillShipment(undefined);
    setOpenBookShipmentDrawer(true);
  };

  const handleQuoteShipment = () => {
    setQuoteDrawerMode('quote');
    setQuotePrefillShipment(undefined);
    setOpenQuoteShipmentDrawer(true);
  };

  const handleQuoteFromShipment = (shipment: SingleShipmentDataExtended) => {
    setQuoteDrawerMode('quote');
    setQuotePrefillShipment(shipment);
    setOpenQuoteShipmentDrawer(true);
  };

  const handleGenerateReturnLabel = (shipment: SingleShipmentDataExtended) => {
    setQuoteDrawerMode('return');
    setQuotePrefillShipment(shipment);
    setOpenQuoteShipmentDrawer(true);
  };

  const handleBookFromShipment = (shipment: SingleShipmentDataExtended) => {
    setBookPrefillShipment(shipment);
    setOpenBookShipmentDrawer(true);
  };

  const handleCreateShellShipment = () => {
    setOpenCreateShellDrawer(true);
  };

  const handleConnectButtonClick = () => {
    navigate('/settings/couriers');
  };

  const onCreateShipmentSuccess = (data: Shipment) => {
    setNewShipmentData((prev) => [...prev, data]);
    newShipmentIdsRef.current.push(data.id);
  };

  const onEditShipmentSuccess = (data: Shipment) => {
    setNewShipmentData((prev) => {
      const index = prev.findIndex((item) => item.id === data.id);
      if (index !== -1) {
        const updated = [...prev];
        updated[index] = data;
        return updated;
      }
      return [...prev];
    });

    fetchShipments(filters.appliedSearchQuery ?? '', false, page, pageSize);
  };

  const getSummaryTabId = useCallback(
    (tabId: string = activeTabId): string => {
      const customTab = customTabs.find((tab) => tab.id === tabId);
      return customTab?.savedFilterSet?.id || tabId;
    },
    [activeTabId, customTabs]
  );

  const fetchShipmentSummary = useCallback(
    (_runSpin = false, tabId?: string) => {
      return GetShipmentSummary(token, getSummaryTabId(tabId))
        .then((res: SummaryApiResponse) => {
          const apiData = res.data;
          const envelope = apiData?.data;

          if (envelope?.is_success) {
            const payload = envelope.data as SummaryData | undefined;
            if (!payload) return;

            const serialized = JSON.stringify(payload);
            if (serialized !== lastSummaryPayloadRef.current) {
              lastSummaryPayloadRef.current = serialized;
              setSummaryData(payload);
            }

            lastSummaryFetchRef.current = Date.now();
          } else if (envelope?.message) {
            message.error(envelope.message);
          }
        })
        .catch((error) => {
          unauthorize(error);
        });
    },
    [getSummaryTabId, token, unauthorize]
  );

  fetchRef.current = fetchShipments;

  const refreshShipmentsTick = useCallback(async () => {
    if (!isTabVisibleRef.current) return;
    if (isCalendarActiveRef.current) return;
    const now = Date.now();
    const forceRefresh =
      lastShipmentsFetchRef.current === 0 ||
      now - lastShipmentsFetchRef.current >= LIST_FORCE_REFRESH_MS;

    if (forceRefresh) {
      await fetchShipments(
        searchQueryRef.current,
        false,
        pageRef.current,
        pageSizeRef.current
      );
      return;
    }

    try {
      const res = (await GetShipmentsVersion(
        token,
        shipmentsVersionRef.current ?? undefined
      )) as unknown as ShipmentsVersionApiResponse;

      const payload = res?.data?.data;
      const changed = payload?.changed;
      const versionVal = payload?.version;

      if (typeof versionVal === 'number') {
        shipmentsVersionRef.current = versionVal;
      }

      if (changed === true || changed === undefined) {
        await fetchShipments(
          searchQueryRef.current,
          false,
          pageRef.current,
          pageSizeRef.current
        );
        if (typeof versionVal === 'number') {
          shipmentsVersionRef.current = versionVal;
        }
      }
    } catch {
      await fetchShipments(
        searchQueryRef.current,
        false,
        pageRef.current,
        pageSizeRef.current
      );
    }
  }, [fetchShipments, token]);

  const refreshSummaryTick = useCallback(async () => {
    if (!isTabVisibleRef.current) return;
    await fetchShipmentSummary(false);
  }, [fetchShipmentSummary]);

  const shipmentSuccess = () => {
    setPage(1);
    fetchShipments(filters.appliedSearchQuery ?? '', true, 1);
    fetchShipmentSummary(true);
  };

  const toggleDrawer: ToggleDrawerFn = useCallback(
    (state, name, shipmentId, alert, nextLegType) => {
      const isOpening = state === 'open';

      setDrawerState({
        isOpen: isOpening,
        name,
        shipmentId: isOpening ? shipmentId : null,
        ...(alert ? { alert } : { alert: null }),
      });

      setSelectedShipmentID(isOpening ? shipmentId : null);
      setDrawerLegType(isOpening ? nextLegType : undefined);

      if (!isOpening) {
        setHighlightedShipmentId(null);

        if (returnToMoreDrawerRef.current) {
          returnToMoreDrawerRef.current = false;
          setMoreDrawerOpen(true);
        }
      }
    },
    []
  );

  useEffect(() => {
    if (shipmentNav?.drawerState === 'open' && shipmentNav?.shipmentId) {
      toggleDrawer('open', 'Detail', shipmentNav.shipmentId, {
        CoC_ID: shipmentNav.CoC_ID,
      });

      dispatch(clearShipmentNav());
    }
  }, [
    shipmentNav?.drawerState,
    shipmentNav?.shipmentId,
    shipmentNav?.CoC_ID,
    toggleDrawer,
    dispatch,
  ]);

  const handleCardToggleDrawer = (
    action: 'open' | 'close',
    type: string,
    id: string | null,
    alert?: unknown,
    nextLegType?: 'CL' | 'EL'
  ) => {
    if (action === 'open' && type === 'Detail') {
      setHighlightedShipmentId(null);
    }
    setDrawerSource('list');
    toggleDrawer(action, type, id, alert, nextLegType);
  };

  const handleCloseDrawer = () => {
    setOpenCreateShipmentDrawer(false);
    setPage(1);
    pageRef.current = 1;

    fetchShipments(filters.appliedSearchQuery ?? '', true, 1, pageSize);

    form.resetFields();
  };

  useEffect(() => {
    const onCloseOverlays = () => {
      toggleDrawer('close', '', null, null);
      setMoreDrawerOpen(false);
      moreDrawerPageRef.current = 1;
      setOpenCreateShipmentDrawer(false);
      setShowBulkUploadModal(false);
    };
    window.addEventListener(ZOOMLOGI_CLOSE_UI_OVERLAYS_EVENT, onCloseOverlays);
    return () =>
      window.removeEventListener(
        ZOOMLOGI_CLOSE_UI_OVERLAYS_EVENT,
        onCloseOverlays
      );
  }, [toggleDrawer]);

  const handleRunSearchFromPanel = useCallback(
    ({
      query,
      check,
      pageSize: ps,
      calledFrom,
      payload,
    }: {
      query: string;
      check: boolean;
      page?: number;
      pageSize?: number;
      calledFrom?: string;
      payload?: ShipmentFiltersPayload | null;
    }) => {
      const trimmed = (query ?? '').trim();

      if (calledFrom === 'search') {
        setAppliedSearchQuery(trimmed);
      }

      setPage(1);
      pageRef.current = 1;

      fetchShipments(
        trimmed,
        check,
        1,
        ps ?? pageSize,
        calledFrom,
        false,
        null,
        payload ?? null
      );
    },
    [fetchShipments, pageSize, setAppliedSearchQuery, setPage]
  );

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  const applyQuickFilter = useCallback(
    ({
      filterBy,
      groupTitle,
      items,
      append = true,
      filterValueOverride,
    }: {
      filterBy: string;
      groupTitle?: string;
      items: Array<{ id: string; name: string; title?: string }>;
      append?: boolean;
      filterValueOverride?: string[];
    }) => {
      const idsForArray = filterValueOverride ?? items.map((x) => x.id);

      filters.setAppliedFilters((prev) => {
        const next = append
          ? [...prev]
          : prev.filter((p) => p.filterBy !== filterBy);

        const toAdd = items
          .filter(
            (it) => !next.some((p) => p.filterBy === filterBy && p.id === it.id)
          )
          .map((it) => ({
            id: it.id,
            name: it.name,
            title: it.title ?? it.name,
            filterBy,
            groupTitle: groupTitle ?? '',
            source: 'manual' as const,
          }));

        return [...next, ...toAdd];
      });

      filters.setFilterArray((prev) => {
        const next = append
          ? [...prev]
          : prev.filter((f) => f.filterBy !== filterBy);

        return [
          ...next.filter((f) => f.filterBy !== filterBy),
          { filterBy, filterValue: idsForArray, source: 'manual' as const },
        ];
      });

      const payloadOverride = {
        ...(filters.filtersPayloadRef.current ?? {}),
        ...(filterBy === 'missing_pod'
          ? { missing_pod: filterValueOverride ?? [] }
          : { [filterBy]: idsForArray }),
      };

      pageRef.current = 1;
      setPage(1);

      fetchShipments(
        filters.appliedSearchQuery ?? '',
        true,
        1,
        pageSize,
        'quick_filter',
        false,
        null,
        payloadOverride
      );
    },
    [filters, fetchShipments, pageSize, setPage]
  );

  useEffect(() => {
    searchQueryRef.current = (filters.appliedSearchQuery ?? '').trim();
  }, [filters.appliedSearchQuery]);

  useEffect(() => {
    searchVisibleRef.current = filters.isSearchVisible;
  }, [filters.isSearchVisible]);

  useShipmentsEffects({
    location,
    drawerStateFromHome,
    setDrawerStateFormHome,
    page,
    pageSize,
    isSearchVisible: filters.isSearchVisible,
    searchQuery: filters.searchQuery,
    startAlertsInterval,
    pollIntervalMs: LIST_POLL_INTERVAL_MS,
    fetchShipments,
    fetchShipmentSummary,
    refreshShipmentsTick,
    refreshSummaryTick,
    drawerState,
    setDrawerState,
    toggleDrawer,
    pageRef,
    pageSizeRef,
    isTabVisibleRef,
    searchVisibleRef,
    searchQueryRef,
    didRunInitialFetchRef,
    didInitialQueryStringEffect,
    fetchRef,
  });

  const filtersKey = useMemo(() => {
    return JSON.stringify({
      applied: (filters.appliedFilters ?? []).map((f) => ({
        id: f.id,
        name: f.name,
        title: f.title,
        filterBy: f.filterBy,
        groupTitle: f.groupTitle,
      })),
    });
  }, [filters.appliedFilters]);

  const calendarSearchQuery = useMemo(() => {
    return isSearchTriggered ? (filters.appliedSearchQuery ?? '').trim() : '';
  }, [filters.appliedSearchQuery, isSearchTriggered]);
  const payloadHasFilters = (() => {
    const payload = filters.filtersPayloadRef?.current;

    if (!payload || typeof payload !== 'object') return false;

    return Object.entries(payload).some(([key, value]) => {
      if (key === 'tab_id') return false;
      if (key === 'search') return false;
      if (key === 'page') return false;
      if (key === 'page_size') return false;

      if (Array.isArray(value)) return value.length > 0;
      if (value && typeof value === 'object')
        return Object.keys(value).length > 0;

      return Boolean(value);
    });
  })();

  const hasActiveFilters = useMemo(() => {
    const isCustomTabActive =
      activeTabId !== 'all_shipments' && activeTabId !== 'active';

    return (
      isCustomTabActive ||
      isSearchTriggered ||
      (filters.appliedFilters?.length ?? 0) > 0 ||
      (filters.filterArray?.length ?? 0) > 0 ||
      payloadHasFilters
    );
  }, [
    activeTabId,
    isSearchTriggered,
    filters.appliedFilters,
    filters.filterArray,
    payloadHasFilters,
  ]);
  const {
    calendarData,
    calendarCounts,
    fetchCalendarShipments,
    viewBy,
    setViewBy,
    calendarTotalShipments,
  } = useCalendarShipments({
    token,
    unauthorize,
    filtersPayloadRef: filters.filtersPayloadRef,
    activeTabId,
    enabled: isCalendarActive,
    filtersKey,
    hasActiveFilters,
    searchQuery: calendarSearchQuery,
  });

  const handleTabChange = useCallback(
    (tabId: string, tabOverride?: ShipmentTab) => {
      dispatch(setActiveTabId(tabId));

      const customTab =
        tabOverride ?? customTabs.find((tab) => tab.id === tabId);
      lastSummaryPayloadRef.current = null;
      void fetchShipmentSummary(true, tabId);

      if (customTab) {
        const baseAppliedFilters: AppliedFilter[] = (
          customTab.savedFilterSet?.appliedFilters ?? []
        ).map((filter) => ({
          ...filter,
          source: 'tab_base' as const,
        }));

        const baseFilters: FilterArrayItem[] = (
          customTab.savedFilterSet?.filters ?? []
        ).map((filter) => ({
          ...filter,
          source: 'tab_base' as const,
        }));

        const visibleAdditionalApplied: AppliedFilter[] = (
          additionalFiltersPerTab[tabId] ?? []
        ).map((filter) => ({
          ...filter,
          source: 'manual' as const,
        }));

        const visibleAdditionalArray: FilterArrayItem[] = (
          additionalFiltersPerTab[tabId] ?? []
        ).map((filter) => {
          const rawValue = (
            filter as {
              filterBy?: string;
              filterValue?: ID[] | string | number;
            }
          ).filterValue;

          return {
            filterBy: (filter as { filterBy?: string }).filterBy ?? '',
            filterValue:
              Array.isArray(rawValue) ||
              typeof rawValue === 'string' ||
              typeof rawValue === 'number'
                ? rawValue
                : [],
            source: 'manual' as const,
          };
        });
        setAppliedFilters(visibleAdditionalApplied);
        setFilterArray(visibleAdditionalArray as never);

        const payload = buildShipmentFiltersPayload(
          [...baseFilters, ...visibleAdditionalArray] as never,
          [...baseAppliedFilters, ...visibleAdditionalApplied]
        );

        filters.setFiltersPayload(payload);
        pageRef.current = 1;
        setPage(1);

        if (isCalendarActiveRef.current) {
          void fetchCalendarShipments(
            undefined,
            payload,
            calendarRangeRef.current ?? undefined,
            true,
            calendarViewTypeRef.current,
            tabId
          );
          return;
        }

        fetchRef.current(
          filters.appliedSearchQuery ?? '',
          true,
          1,
          pageSize,
          'tab_change',
          false,
          null,
          payload,
          tabId
        );
        return;
      }

      // Default tabs
      setAppliedFilters([]);
      setFilterArray([]);
      filters.setFiltersPayload(null);
      pageRef.current = 1;
      setPage(1);

      if (isCalendarActiveRef.current) {
        void fetchCalendarShipments(
          undefined,
          null,
          calendarRangeRef.current ?? undefined,
          true,
          calendarViewTypeRef.current,
          tabId
        );
        return;
      }

      fetchRef.current(
        filters.appliedSearchQuery ?? '',
        true,
        1,
        pageSize,
        'tab_change',
        false,
        null,
        null,
        tabId
      );
    },
    [
      dispatch,
      customTabs,
      additionalFiltersPerTab,
      setAppliedFilters,
      setFilterArray,
      filters,
      pageSize,
      setPage,
      fetchCalendarShipments,
      fetchShipmentSummary,
    ]
  );
  const filtersForSavedFilterSetModal = useMemo(() => {
    const customTab = customTabs.find((tab) => tab.id === activeTabId);

    if (!customTab) {
      return {
        currentAppliedFilters: filters.appliedFilters ?? [],
        currentFilterArray: filters.filterArray ?? [],
      };
    }

    const baseAppliedFilters = customTab.savedFilterSet?.appliedFilters ?? [];
    const baseFilters = customTab.savedFilterSet?.filters ?? [];

    const appliedSeen = new Set<string>();
    const mergedAppliedFilters: AppliedFilter[] = [];

    [...baseAppliedFilters, ...(filters.appliedFilters ?? [])].forEach((f) => {
      const key = `${String(f.id)}::${f.filterBy ?? ''}::${f.dateFilter ?? ''}`;
      if (!appliedSeen.has(key)) {
        appliedSeen.add(key);
        mergedAppliedFilters.push(f);
      }
    });

    const filterArrayMap = new Map<string, Set<string | number>>();
    const filterArrayMetadata = new Map<
      string,
      Pick<FilterArrayItem, 'logic' | 'source'>
    >();

    [...baseFilters, ...(filters.filterArray ?? [])].forEach((item) => {
      const key = item.filterBy;
      if (!key) return;

      const existing = filterArrayMap.get(key) ?? new Set<string | number>();
      const values = Array.isArray(item.filterValue)
        ? item.filterValue
        : [item.filterValue];

      values.forEach((v) => existing.add(v));
      filterArrayMap.set(key, existing);
      filterArrayMetadata.set(key, {
        ...(item.logic ? { logic: item.logic } : {}),
        ...(item.source ? { source: item.source } : {}),
      });
    });

    const mergedFilterArray = Array.from(filterArrayMap.entries()).map(
      ([filterBy, values]) => ({
        filterBy,
        filterValue: Array.from(values),
        ...filterArrayMetadata.get(filterBy),
      })
    );

    return {
      currentAppliedFilters: mergedAppliedFilters,
      currentFilterArray: mergedFilterArray,
    };
  }, [activeTabId, customTabs, filters.appliedFilters, filters.filterArray]);

  const massActionFiltersPayload = useMemo(() => {
    return buildMassActionFiltersPayload({
      activeTabId,
      appliedFilters: filters.appliedFilters ?? [],
      filterArray: filters.filterArray ?? [],
      customTabs,
      currentPayload: filters.filtersPayloadRef?.current ?? null,
    });
  }, [
    activeTabId,
    customTabs,
    filters.appliedFilters,
    filters.filterArray,
    filters.filtersPayloadRef,
  ]);

  const {
    savedFilterModalOpen,
    editingFilterSet,
    openCreateModal,
    openEditModal,
    closeModal,
    handleSave,
    handleDelete,
  } = useShipmentSavedFilterSetModal({
    token,
    dispatch,
    activeTabId,
    onTabChange: handleTabChange,
  });
  const calendarViewport = useCalendarViewportCounts(calendarData ?? []);
  const { handleRangeChange: calendarHandleRangeChange } = calendarViewport;
  const calendarRangeRef = useRef<{ start: string; end: string } | null>(null);
  const calendarViewTypeRef = useRef<string>('dayGridMonth');
  const toolbarFiltersPayload =
    isCalendarActive && calendarRangeRef.current
      ? {
          ...massActionFiltersPayload,
          start: calendarRangeRef.current.start,
          end: calendarRangeRef.current.end,
          view_by: viewBy,
        }
      : massActionFiltersPayload;

  const calendarToolbarTotal = useMemo(() => {
    if (activeViewType !== 'calendar') return 0;

    return calendarTotalShipments ?? 0;
  }, [activeViewType, calendarTotalShipments]);

  const calendarSummaryOverride = useMemo(() => {
    if (activeViewType !== 'calendar') return null;

    return {
      is_critical: calendarCounts?.critical ?? 0,
      is_risk: calendarCounts?.at_risk ?? 0,
      is_track: calendarCounts?.no_issue ?? 0,
    };
  }, [activeViewType, calendarCounts]);

  const shipmentSelectionScopeCount = useMemo(
    () =>
      getSelectionScopeCount({
        activeViewType,
        activeTabId,
        hasActiveFilters,
        isSearchTriggered,
        calendarTotalShipments,
        calendarToolbarTotal,
        totalShipments,
        overAllShipmentCount,
      }),
    [
      activeViewType,
      activeTabId,
      hasActiveFilters,
      isSearchTriggered,
      calendarTotalShipments,
      calendarToolbarTotal,
      totalShipments,
      overAllShipmentCount,
    ]
  );

  const hasToolbarData =
    activeViewType === 'calendar'
      ? (calendarData ?? []).length > 0
      : (filteredData ?? []).length > 0;

  const isCalendarView = activeViewType === 'calendar';
  const hasCalendarData = (calendarData ?? []).length > 0;
  const hasListData = shipmentsData.length > 0;
  const hasAnyData = isCalendarView ? hasCalendarData : hasListData;

  const shouldShowInitialLoading =
    !isCalendarView && isLoadingShipments && !hasAnyData;

  const shouldShowEmptyShipments =
    !isCalendarView &&
    !isLoadingShipments &&
    !hasAnyData &&
    activeTabId === 'all_shipments' &&
    !hasActiveFilters;
  const shouldShowToolbar =
    (hasToolbarData || hasActiveFilters) &&
    allPermissions?.is_view &&
    decodedToken?.company_name?.toLowerCase() !== 'zoomlogi support';
  const shouldShowMainLayout =
    isCalendarView || (!shouldShowInitialLoading && !shouldShowEmptyShipments);
  const availableHourSlots = useMemo(() => {
    const slots: string[] = [];

    (calendarData ?? []).forEach((dayAgg) => {
      (dayAgg.hour_aggregates ?? []).forEach((h) => {
        if (Number(h.total ?? 0) > 0 && h.hour) slots.push(String(h.hour));
      });
    });

    return slots.sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());
  }, [calendarData]);

  const handleCalendarRangeChange = useCallback(
    (arg: unknown) => {
      calendarHandleRangeChange(
        arg as Parameters<typeof calendarHandleRangeChange>[0]
      );

      const a = arg as {
        startStr?: string;
        endStr?: string;
        viewType?: string;
      };
      if (!a?.startStr || !a?.endStr) return;
      // Track current FullCalendar view type for re-fetches triggered by
      // filter/viewBy changes (those won't come through onRangeChange).
      if (a.viewType) {
        calendarViewTypeRef.current = a.viewType;
      }
      // Normalise to plain YYYY-MM-DD regardless of whether FullCalendar
      // returns a date-only string or a datetime with a timezone offset
      // (e.g. "2025-11-30T00:00:00+05:00").  The backend only needs dates.
      const startDate = a.startStr.slice(0, 10);
      const exclusiveEndDate = a.endStr.slice(0, 10);

      // FullCalendar's end is exclusive — subtract one day to make it inclusive.
      const [y, m, d] = exclusiveEndDate.split('-').map(Number);
      const prev = new Date(Date.UTC(y, m - 1, d - 1));
      const inclusiveEnd = prev.toISOString().slice(0, 10);

      const newRange = { start: startDate, end: inclusiveEnd };
      calendarRangeRef.current = newRange;

      if (!isCalendarActiveRef.current) return;

      fetchCalendarShipments(
        undefined,
        filters.filtersPayloadRef?.current ?? null,
        newRange,
        undefined,
        a.viewType
      );
    },
    [
      calendarHandleRangeChange,
      fetchCalendarShipments,
      filters.filtersPayloadRef,
    ]
  );

  useEffect(() => {
    if (!isCalendarActive) return;
    const range = calendarRangeRef.current;
    if (!range) return;
    fetchCalendarShipments(
      viewBy,
      filters.filtersPayloadRef?.current ?? null,
      range,
      undefined,
      calendarViewTypeRef.current
    );
  }, [
    isCalendarActive,
    viewBy,
    filtersKey,
    calendarSearchQuery,
    fetchCalendarShipments,
    filters.filtersPayloadRef,
    // NOTE: calendarRangeRef is a ref — range-change fetches are triggered
    // by handleCalendarRangeChange above, not this effect.
  ]);

  const buttonsList = [
    { id: 'list', name: 'List', icon: <ListIcon /> },
    { id: 'calendar', name: 'Calendar', icon: <CalendarIcon /> },
  ];

  const handleChangeViewType = (val: string) => {
    if (drawerState.isOpen) {
      toggleDrawer('close', '', null, null);
      setHighlightedShipmentId(null);
      setDrawerSource(val === 'calendar' ? 'calendar' : 'list');
    }

    if (val !== activeViewType && selectAllAcrossFilters) {
      setSelectAllAcrossFilters(false);
      setExcludedIds([]);
    }

    setActiveViewType(val as 'list' | 'calendar');
  };

  const didSwitchToListRef = useRef(false);

  useEffect(() => {
    if (isCalendarActive) {
      didSwitchToListRef.current = true;
      return;
    }
    // we just switched from calendar -> list
    if (didSwitchToListRef.current) {
      didSwitchToListRef.current = false;

      fetchShipments(
        filters.appliedSearchQuery ?? '',
        true,
        pageRef.current,
        pageSizeRef.current
      );
    }
  }, [isCalendarActive, fetchShipments, filters.appliedSearchQuery]);

  const selectableItems = useMemo(() => {
    if (activeViewType === 'calendar') {
      return (calendarData ?? []).flatMap((agg) =>
        agg?.top_shipments?.map((it) => ({
          id: String(it.shipment_id),
          status: it.status,
        }))
      );
    }
    return (filteredData ?? []).map((it) => ({
      id: it.id,
      status: it.status,
    }));
  }, [activeViewType, calendarData, filteredData]);

  const isSelectAllMode =
    activeViewType === 'list' && !!selectAllAcrossFilters && !isCalendarActive;
  const isCalendarSelectAllMode = isCalendarActive && !!selectAllAcrossFilters;
  const handleDeleteSavedFilterSetFromDropdown = useCallback(
    async (filterSetId: string) => {
      const linkedTab = customTabs.find(
        (tab) => tab.savedFilterSet?.id === filterSetId
      );

      await handleDelete({
        filterSet: { id: filterSetId },
        tabId: linkedTab?.id ?? null,
      });
    },
    [customTabs, handleDelete]
  );
  return (
    <>
      <div
        className={`${
          drawerSource === 'calendar'
            ? 'shipment-section-calendar'
            : 'shipment-section'
        } ${drawerState.isOpen ? 'open' : ''}`}
        style={{ paddingBottom: '50px' }}
      >
        <DashboardTitle
          title={t('title')}
          showAddButton={!hideNewShipmentButton}
          showTrack={manualShipmentFlags.track}
          onAddButtonClick={handleShipmentFormModal}
          onBulkUpload={handleBulkUpload}
          onBookShipment={
            manualShipmentFlags.book ? handleBookShipment : undefined
          }
          onQuoteShipment={
            manualShipmentFlags.quote ? handleQuoteShipment : undefined
          }
          onCreateShellShipment={
            manualShipmentFlags.create ? handleCreateShellShipment : undefined
          }
          bookShipmentMenuDisabled={bookShipmentMenuDisabled}
          allPermissions={allPermissions}
          tabsSlot={
            <TabStrip
              permissions={permissions}
              token={token}
              activeTabCount={totalShipments ?? 0}
              onTabChange={handleTabChange}
              currentAppliedFilters={filters.appliedFilters}
              currentFilterArray={filters.filterArray}
              onOpenCreateModal={openCreateModal}
              onOpenEditModal={openEditModal}
              onOpenViewFiltersModal={handleOpenViewFiltersModal}
            />
          }
        />

        {(shouldShowInitialLoading || shouldShowEmptyShipments) && (
          <EmptyShipments
            handleConnectButtonClick={handleConnectButtonClick}
            handleShipmentFormModal={handleShipmentFormModal}
            allPermissions={allPermissions}
            showTrackManually={manualShipmentFlags.track}
            isLoading={shouldShowInitialLoading}
          />
        )}

        {shouldShowMainLayout && (
          <>
            <Flex style={{ overflowX: 'hidden', display: 'block' }}>
              <div>
                <SummaryCards
                  isCalendarView={activeViewType === 'calendar'}
                  calendarSummaryOverride={calendarSummaryOverride}
                  drawerSource={drawerSource}
                  summaryData={summaryData}
                  isSupportCompany={isSupportCompany}
                  panelApplyFilters={filters.runPanelApplyFilters}
                  drawerOpen={drawerState.isOpen}
                />

                <Row
                  gutter={16}
                  align='middle'
                  className='custom-ant-row-filters'
                  wrap={false}
                >
                  {shouldShowToolbar && (
                    <Col flex={'0 0 auto'}>
                      <ShipmentToolbar
                        activeTabId={activeTabId}
                        overAllShipmentCount={overAllShipmentCount}
                        selectAllAcrossFilters={selectAllAcrossFilters}
                        setSelectAllAcrossFilters={setSelectAllAcrossFilters}
                        excludedIds={excludedIds}
                        setExcludedIds={setExcludedIds}
                        appliedSearchQuery={(
                          filters.appliedSearchQuery ?? ''
                        ).trim()}
                        filtersPayload={toolbarFiltersPayload}
                        refreshCurrentView={() => {
                          if (activeViewType === 'calendar') {
                            const customTab = customTabs.find(
                              (tab) => tab.id === activeTabId
                            );

                            let payload =
                              filters.filtersPayloadRef?.current ?? null;

                            if (customTab) {
                              payload = buildShipmentFiltersPayload(
                                [
                                  ...(customTab.savedFilterSet?.filters ?? []),
                                  ...(filters.filterArray ?? []),
                                ] as never,
                                [
                                  ...(customTab.savedFilterSet
                                    ?.appliedFilters ?? []),
                                  ...(filters.appliedFilters ?? []),
                                ]
                              );
                            }

                            fetchCalendarShipments(
                              viewBy,
                              payload,
                              calendarRangeRef.current ?? undefined,
                              true
                            );
                          } else {
                            fetchShipments(
                              filters.appliedSearchQuery ?? '',
                              false,
                              page,
                              pageSize,
                              null
                            );
                          }
                        }}
                        isCalendarActive={activeViewType === 'calendar'}
                        getViewportCalendarIds={() => {
                          return getVisibleCalendarShipmentIds(
                            calendarData,
                            calendarRangeRef.current
                          );
                        }}
                        selectableItems={selectableItems}
                        onApplyQuickFilter={applyQuickFilter}
                        isSearchTriggered={isSearchTriggered}
                        setSelectedShipmentStatuses={
                          setSelectedShipmentStatuses
                        }
                        appliedFilters={filters.appliedFilters}
                        selectedShipmentStatuses={selectedShipmentStatuses}
                        searchQuery={filters.searchQuery}
                        totalShipments={
                          activeViewType === 'calendar'
                            ? hasActiveFilters
                              ? (calendarTotalShipments ?? 0)
                              : calendarToolbarTotal
                            : (totalShipments ?? 0)
                        }
                        selectionScopeCount={shipmentSelectionScopeCount}
                        CT_all_permissions={allPermissions}
                        page={page}
                        pageSize={pageSize}
                        fetchShipments={fetchShipments}
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                        checked={checked}
                        setChecked={setChecked}
                        setShipmentsData={setShipmentsData}
                        setFilteredData={setFilteredData}
                        filteredData={filteredData}
                        isDrawerOpen={drawerState.isOpen}
                        toggleDrawer={toggleDrawer}
                      />
                    </Col>
                  )}

                  <Col flex={'auto'}>
                    {/* Tab strip — sits above the filter panel */}
                    <ShipmentsFilterPanel
                      onDeleteSavedFilterSet={
                        handleDeleteSavedFilterSetFromDropdown
                      }
                      dropdownMaxHeight={dynamicDropdownHeight}
                      appliedSearchQuery={filters.appliedSearchQuery}
                      setAppliedSearchQuery={filters.setAppliedSearchQuery}
                      savedFilterKey='shipment'
                      auditSearch
                      token={token}
                      permissions={permissions}
                      appliedFilters={filters.appliedFilters}
                      setAppliedFilters={filters.setAppliedFilters}
                      filterArray={filters.filterArray}
                      setFilterArray={filters.setFilterArray}
                      setFiltersPayload={filters.setFiltersPayload}
                      searchQuery={filters.searchQuery}
                      setSearchQuery={filters.setSearchQuery}
                      isSearchVisible={filters.isSearchVisible}
                      setIsSearchVisible={filters.setIsSearchVisible}
                      page={page}
                      pageSize={pageSize}
                      onRunSearch={handleRunSearchFromPanel}
                      registerHandleApplyFilters={
                        filters.registerHandleApplyFilters
                      }
                      didRunInitialFetchRef={didRunInitialFetchRef}
                      useInternalSaveFilterSetModal={false}
                      onOpenSaveFilterSet={openCreateModal}
                      activeTabId={activeTabId}
                      customTabs={customTabs}
                    />
                  </Col>

                  <Col className='switch-col'>
                    <SwitchButtons
                      isIcons={true}
                      options={buttonsList}
                      defaultActive={activeViewType}
                      onChange={handleChangeViewType}
                    />
                  </Col>
                </Row>

                {activeViewType === 'calendar' ? (
                  <div style={{ height: 'fit-content', marginTop: 12 }}>
                    <CalendarView
                      tableView={false}
                      calendarApiRef={calendarApiRef}
                      viewMode='month'
                      filteredData={calendarData}
                      checked={checked}
                      selectedIds={selectedIds}
                      isSelectAllMode={isCalendarSelectAllMode}
                      excludedIds={excludedIds}
                      viewBy={viewBy}
                      setViewBy={setViewBy}
                      onRangeChange={handleCalendarRangeChange}
                      onToggleSelect={(item, nextSelected) => {
                        const id = String(item.shipment_id);
                        if (isCalendarSelectAllMode) {
                          setExcludedIds((prev) => {
                            if (nextSelected)
                              return prev.filter((curr) => curr !== id);
                            if (prev.includes(id)) return prev;
                            return [...prev, id];
                          });
                          return;
                        }
                        const fakeShipmentCard = calendarItemToShipment(item);
                        handleSelect(id, nextSelected, fakeShipmentCard);
                      }}
                      onShipmentClick={(shipmentId, alertName, shipment) => {
                        setDrawerSource('calendar');
                        setHighlightedShipmentId(null);
                        toggleDrawer(
                          'open',
                          'Detail',
                          shipmentId,
                          alertName,
                          shipment.leg_type
                        );
                      }}
                      onMoreClick={({
                        dateStr,
                        eventDatetime,
                        slotStart,
                        slotEnd,
                        source,
                      }) => {
                        moreDrawerSortRef.current = null;
                        moreDrawerPageRef.current = 1;

                        setMoreDrawerSource(source);
                        toggleDrawer('close', '', null, null);
                        setMoreDrawerViewBy(viewBy);
                        setMoreDrawerEventDatetime(eventDatetime ?? dateStr);
                        setMoreDrawerSlotStart(slotStart ?? null);
                        setMoreDrawerSlotEnd(slotEnd ?? null);
                        setMoreDrawerOpen(true);
                      }}
                    />
                  </div>
                ) : (
                  <>
                    <Flex vertical style={{ marginTop: 12 }}>
                      {newShipmentData.map((card) => (
                        <AnimatedShipmentCard
                          key={card.id}
                          card={card}
                          toggleDrawer={toggleDrawer}
                          drawerState={drawerState}
                          openShipmentId={drawerState.shipmentId}
                          onExpire={(id: string) => {
                            setNewShipmentData((prev) =>
                              prev.filter((c) => c.id !== id)
                            );
                            newShipmentIdsRef.current =
                              newShipmentIdsRef.current.filter(
                                (currId) => currId !== id
                              );

                            fetchShipments(
                              filters.appliedSearchQuery ?? '',
                              true,
                              page,
                              pageSize
                            );
                          }}
                        />
                      ))}
                    </Flex>

                    {filteredData?.length > 0 && (
                      <Row
                        gutter={16}
                        style={{ marginBottom: 16, marginTop: 12 }}
                      >
                        <Col span={24}>
                          <Flex vertical gap={16}>
                            {filteredData.map((card) => (
                              <div
                                key={card.id}
                                id={`shipment-card-${card.id}`}
                              >
                                <ShipmentCard
                                  checked={checked}
                                  selected={
                                    isSelectAllMode
                                      ? !excludedIds.includes(card.id)
                                      : selectedIds.includes(card.id)
                                  }
                                  onSelectChange={(
                                    id,
                                    nextSelected,
                                    shipment
                                  ) => {
                                    if (isSelectAllMode) {
                                      setExcludedIds((prev) => {
                                        if (nextSelected) {
                                          return prev.filter(
                                            (curr) => curr !== id
                                          );
                                        }
                                        if (prev.includes(id)) return prev;
                                        return [...prev, id];
                                      });
                                      return;
                                    }

                                    handleSelect(id, nextSelected, shipment);
                                  }}
                                  card={card}
                                  toggleDrawer={handleCardToggleDrawer}
                                  drawerState={drawerState}
                                  openShipmentId={effectiveOpenShipmentId}
                                  highlighted={
                                    highlightedShipmentId === card.id
                                  }
                                />
                              </div>
                            ))}
                          </Flex>
                        </Col>
                      </Row>
                    )}

                    {filteredData.length === 0 && (
                      <Flex
                        align='center'
                        justify='center'
                        style={{ height: '50vh', marginTop: 12 }}
                      >
                        <CustomParagraph text={t('noDataAvailable')} />
                      </Flex>
                    )}
                  </>
                )}
              </div>

              <ShipmentDrawer
                onLinkedShipmentClick={handleHighlightShipmentCard}
                allPermissions={allPermissions}
                drawerState={drawerState}
                legType={drawerLegType}
                toggleDrawer={toggleDrawer}
                shipmentSuccess={shipmentSuccess}
                filterByLinkCode={filterByLinkCode}
                onEditShipmentSuccess={onEditShipmentSuccess}
                onQuoteFromShipment={
                  manualShipmentFlags.quote
                    ? handleQuoteFromShipment
                    : undefined
                }
                onBookFromShipment={
                  !manualShipmentFlags.book ||
                  !(worldCourierEnabled || cryoportEnabled)
                    ? undefined
                    : handleBookFromShipment
                }
                onGenerateReturnLabel={handleGenerateReturnLabel}
                detailRefreshKey={detailRefreshKey}
              />
            </Flex>
          </>
        )}

        {shipmentsData.length > 0 && activeViewType === 'list' ? (
          <Row style={{ width: '100%', marginTop: 12 }}>
            <Col flex='auto'>
              <Flex justify='flex-end'>
                <CustomPagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </Flex>
            </Col>
          </Row>
        ) : null}

        <BulkUploadModal
          modalOpen={showBulkUploadModal}
          setModalOpen={setShowBulkUploadModal}
        />
      </div>

      {openCreateShipmentDrawer && (
        <CommonDrawer
          open={openCreateShipmentDrawer}
          onClose={() => handleCloseDrawer()}
          width={520}
        >
          <CreateShipmentDrawer
            handleCloseDrawer={handleCloseDrawer}
            onSuccess={onCreateShipmentSuccess}
          />
        </CommonDrawer>
      )}

      {openBookShipmentDrawer && (
        <CommonDrawer
          open={openBookShipmentDrawer}
          onClose={() => {
            setOpenBookShipmentDrawer(false);
            setBookPrefillShipment(undefined);
          }}
          width={520}
        >
          <BookShipmentDrawer
            key={bookPrefillShipment?.id ?? 'menu-book'}
            onClose={() => {
              setOpenBookShipmentDrawer(false);
              setBookPrefillShipment(undefined);
            }}
            shellShipment={bookPrefillShipment}
            onPromoteSuccess={() => {
              shipmentSuccess();
              setDetailRefreshKey((k) => k + 1);
            }}
          />
        </CommonDrawer>
      )}

      {openQuoteShipmentDrawer && (
        <CommonDrawer
          open={openQuoteShipmentDrawer}
          onClose={() => {
            setOpenQuoteShipmentDrawer(false);
            setQuotePrefillShipment(undefined);
            setQuoteDrawerMode('quote');
          }}
          width={520}
        >
          <QuoteShipmentDrawer
            onClose={() => {
              setOpenQuoteShipmentDrawer(false);
              setQuotePrefillShipment(undefined);
              setQuoteDrawerMode('quote');
            }}
            mode={quoteDrawerMode}
            shellShipment={
              quoteDrawerMode === 'quote' ? quotePrefillShipment : undefined
            }
            returnSourceShipment={
              quoteDrawerMode === 'return' ? quotePrefillShipment : undefined
            }
            onPromoteSuccess={() => {
              shipmentSuccess();
              setDetailRefreshKey((k) => k + 1);
            }}
          />
        </CommonDrawer>
      )}

      {openCreateShellDrawer && (
        <CommonDrawer
          open={openCreateShellDrawer}
          onClose={() => setOpenCreateShellDrawer(false)}
          width={520}
        >
          <CreateShellShipmentDrawer
            onClose={() => setOpenCreateShellDrawer(false)}
            onSuccess={(shipment) => {
              shipmentSuccess();
              if (shipment?.id) {
                toggleDrawer('open', 'Detail', shipment.id, null);
              }
            }}
          />
        </CommonDrawer>
      )}

      {moreDrawerOpen && (
        <CommonDrawer
          open={moreDrawerOpen}
          onClose={() => {
            setMoreDrawerOpen(false);
            moreDrawerPageRef.current = 1;
          }}
          width={552}
          className='remove-header-line-calendar-view'
        >
          <div style={{ height: '100%' }}>
            {moreDrawerEventDatetime ? (
              <CalendarViewMore
                appliedFilterArray={filters.filterArray}
                filtersPayload={filters.filtersPayloadRef.current}
                activeTabId={activeTabId}
                availableHourSlots={availableHourSlots}
                checked={checked}
                initialPage={moreDrawerPageRef.current}
                onPageChange={(p) => {
                  moreDrawerPageRef.current = p;
                }}
                initialSortKey={moreDrawerSortRef.current?.key}
                initialSortOrder={moreDrawerSortRef.current?.order}
                onSortChange={(sort) => {
                  moreDrawerSortRef.current = sort;
                }}
                source={moreDrawerSource}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                isSelectAllMode={isCalendarSelectAllMode}
                excludedIds={excludedIds}
                setExcludedIds={setExcludedIds}
                setSelectedShipmentStatuses={setSelectedShipmentStatuses}
                slotStart={moreDrawerSlotStart}
                slotEnd={moreDrawerSlotEnd}
                token={token}
                viewBy={moreDrawerViewBy}
                eventDatetime={moreDrawerEventDatetime}
                onUnauthorized={unauthorize}
                tabValue='timeliness'
                chartType='transit_time'
                onShipmentClick={(shipmentId) => {
                  returnToMoreDrawerRef.current = true;
                  setMoreDrawerOpen(false);
                  setDrawerSource('calendar');
                  setHighlightedShipmentId(null);
                  toggleDrawer('open', 'Detail', shipmentId, {
                    isFromHome: true,
                  });
                }}
                initialSearch={calendarSearchQuery}
                initialTeams={calendarTeamIds}
                initialServiceLevels={calendarCourierIds}
              />
            ) : null}
          </div>
        </CommonDrawer>
      )}
      <SavedFilterSetModal
        permissions={permissions}
        open={savedFilterModalOpen}
        onClose={closeModal}
        onDelete={handleDelete}
        editingFilterSet={editingFilterSet}
        currentAppliedFilters={
          filtersForSavedFilterSetModal.currentAppliedFilters
        }
        currentFilterArray={filtersForSavedFilterSetModal.currentFilterArray}
        onSave={handleSave}
      />
      <ViewFiltersModal
        open={viewFiltersModalOpen}
        tab={viewFiltersTab}
        onClose={handleCloseViewFiltersModal}
      />
    </>
  );
};

export default Shipments;
